'use client';

import dynamic from 'next/dynamic';
import { useApartmentReviewsContext } from './ApartmentReviewsProvider';
import { ReviewsShell, type ReviewSourceConfig } from './ReviewsShell';
import { ReviewsEntryPoint } from './ReviewsEntryPoint';

const ReputationMockPanel = dynamic(() => import('./ReputationMockPanel'));
const GoogleMockPanel = dynamic(() => import('./ProviderMockPanels').then((module) => module.GoogleMockPanel));
const RedditMockPanel = dynamic(() => import('./ProviderMockPanels').then((module) => module.RedditMockPanel));

export function ApartmentReviewsModule() {
  const { lotId, reviews, mockEnabled } = useApartmentReviewsContext();
  if (mockEnabled) {
    return (
      <div>
        <p role="note">Preview · synthetic Figma data for all three providers</p>
        <ReviewsShell
          key={lotId}
          id="reviews"
          sources={[
            { id: 'web', label: 'Web reviews', panel: { status: 'ready', render: () => <ReputationMockPanel /> } },
            { id: 'google', label: 'Google', panel: { status: 'ready', render: () => <GoogleMockPanel /> } },
            { id: 'reddit', label: 'Reddit', panel: { status: 'ready', render: () => <RedditMockPanel /> } },
          ]}
        />
      </div>
    );
  }
  if (!reviews) return null;
  const sources: Array<ReviewSourceConfig> = [];
  if (reviews.reputation) sources.push({ id: 'web', label: 'Web reviews', panel: { status: 'unavailable' } });
  if (reviews.google)
    sources.push({
      id: 'google',
      label: 'Google',
      instanceKey: reviews.google.placeId,
      panel: { status: 'unavailable' },
    });
  if (reviews.reddit) sources.push({ id: 'reddit', label: 'Reddit', panel: { status: 'unavailable' } });
  return <ReviewsShell key={lotId} id="reviews" sources={sources} />;
}

export function ApartmentReviewsEntry() {
  const { reviews, mockEnabled } = useApartmentReviewsContext();
  if (mockEnabled) {
    return (
      <ReviewsEntryPoint
        targetId="reviews"
        hasReputation
        aggregateRating={{ rating: 4.3, reviewCount: 231 }}
        hasGooglePlace
        hasRedditLinks
      />
    );
  }
  if (!reviews) return null;
  const { averageRating, totalReviews } = reviews.reputation ?? {};
  return (
    <ReviewsEntryPoint
      targetId="reviews"
      hasReputation={!!reviews.reputation}
      aggregateRating={
        averageRating !== undefined && totalReviews !== undefined
          ? { rating: averageRating, reviewCount: totalReviews }
          : null
      }
      hasGooglePlace={!!reviews.google}
      hasRedditLinks={!!reviews.reddit?.links.length}
    />
  );
}
